import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CartSidebar } from './CartSidebar';
import { useCart } from '../context/CartContext';
import { scrollToSection, isHomePage } from '../utils/scrollUtils';
import NotificationBell from './NotificationBell';
import { useAuth } from '../context/AuthContext';
import { User, ShoppingBag } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isCartOpen, openCart, closeCart, cartItems } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Función para manejar la navegación
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Si estamos en home, hacer scroll a la sección
      scrollToSection(sectionId);
      setIsMenuOpen(false);
    } else {
      // Si estamos en otra ruta, navegar a home y luego hacer scroll
      navigate('/', { state: { scrollTo: sectionId } });
      setIsMenuOpen(false);
    }
  };

  // Cerrar dropdown de usuario al dar click fuera
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (!isUserMenuOpen) return;
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isUserMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/">
            <img 
              className="h-12 w-auto" 
              src="https://disjteanovqtsuirirkt.supabase.co/storage/v1/object/sign/assets/images/frahda-logo-final.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8yNTk0OTRmYS0xZmVhLTRlNGMtYWY2OS1mNjM2N2UyZGU4NjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhc3NldHMvaW1hZ2VzL2ZyYWhkYS1sb2dvLWZpbmFsLnBuZyIsImlhdCI6MTc2MDg1MjEzMiwiZXhwIjoxNzkyMzg4MTMyfQ.VJVycPU5O0-1GBMVfQuz9ZM0AWxM1w8LHrlQ55vzuYs" 
              alt="Frahda Studio Logo" 
            />
          </Link>

          {/* Navegación de escritorio */}
          <nav className="hidden lg:flex space-x-6 xl:space-x-8">
            <Link to="/products" className="text-gray-700 hover:text-pink-dark transition-colors">TIENDA</Link>
            <Link to="/agendamiento" className="text-gray-700 hover:text-pink-dark transition-colors">AGENDAR CITA</Link>
            <Link 
              to="/" 
              onClick={(e) => handleNavigation(e, 'servicios')}
              className="text-gray-700 hover:text-pink-dark transition-colors"
            >
              SERVICIOS
            </Link>
            <Link to="/cursos" className="text-gray-700 hover:text-pink-dark transition-colors">CURSOS</Link>
            <Link to="/eventos" className="text-gray-700 hover:text-pink-dark transition-colors">EVENTOS</Link>
            <Link 
              to="/" 
              onClick={(e) => handleNavigation(e, 'quienes-somos')}
              className="text-gray-700 hover:text-pink-dark transition-colors"
            >
              QUIÉNES SOMOS
            </Link>
            <Link 
              to="/" 
              onClick={(e) => handleNavigation(e, 'contacto')}
              className="text-gray-700 hover:text-pink-dark transition-colors"
            >
              CONTACTO
            </Link>
          </nav>

          {/* Acciones de usuario y carrito */}
          <div className="flex items-center gap-4">
            {/* Notificaciones: campana con badge */}
            <div className="relative flex items-center">
              <NotificationBell />
            </div>

            {/* User actions (compact to avoid layout shifts) */}
            <div className="relative" ref={userMenuRef}>
              <button
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:text-pink-dark transition-colors"
                aria-label={isAuthenticated ? 'Menú de usuario' : 'Iniciar sesión o registrarse'}
                onClick={() => setIsUserMenuOpen((v) => !v)}
              >
                <User className="w-5 h-5" />
              </button>

              {/* Dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 w-56 rounded-md border border-gray-100 bg-white py-2 shadow-lg">
                  {isAuthenticated ? (
                    <>
                      <div className="px-4 pb-2 pt-1 text-sm text-gray-600">
                        Hola{user?.first_name ? `, ${user.first_name}` : ''}
                      </div>
                      <button
                        onClick={() => { setIsUserMenuOpen(false); navigate('/profile'); }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
                      >
                        <span className="material-icons text-[18px]">person</span>
                        Perfil
                      </button>
                      <button
                        onClick={() => { setIsUserMenuOpen(false); logout(); }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
                      >
                        <span className="material-icons text-[18px]">logout</span>
                        Cerrar sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { setIsUserMenuOpen(false); navigate('/login'); }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
                      >
                        <span className="material-icons text-[18px]">login</span>
                        Ingresar
                      </button>
                      <button
                        onClick={() => { setIsUserMenuOpen(false); navigate('/register'); }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
                      >
                        <span className="material-icons text-[18px]">person_add</span>
                        Crear cuenta
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {/* Carrito con badge y micro-animación */}
              <div className="relative flex items-center">
                <button
                  onClick={openCart}
                  className="relative text-gray-700 hover:text-pink-dark transition-colors"
                  aria-label="Carrito"
                >
                  <ShoppingBag id="cart-icon" className="w-5 h-5" />
                  {cartItems && cartItems.length > 0 && (
                    <span className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-primary-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {cartItems.length > 9 ? '9+' : cartItems.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Botón menú móvil y tablet */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-gray-700 hover:text-pink-dark transition-colors"
              aria-label="Abrir menú"
            >
              <span className="material-icons">{isMenuOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>

        {/* Navegación móvil y tablet */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link 
              to="/products" 
              className={`block px-3 py-2 rounded-lg text-gray-700 hover:text-pink-dark hover:bg-gray-50 transition-all duration-200 ${isMenuOpen ? 'mobile-menu-item' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              TIENDA
            </Link>
            <Link 
              to="/agendamiento" 
              className={`block px-3 py-2 rounded-lg text-gray-700 hover:text-pink-dark hover:bg-gray-50 transition-all duration-200 ${isMenuOpen ? 'mobile-menu-item' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              AGENDAR CITA
            </Link>
            <Link 
              to="/" 
              onClick={(e) => handleNavigation(e, 'servicios')}
              className={`block px-3 py-2 rounded-lg text-gray-700 hover:text-pink-dark hover:bg-gray-50 transition-all duration-200 ${isMenuOpen ? 'mobile-menu-item' : ''}`}
            >
              SERVICIOS
            </Link>
            <Link 
              to="/cursos" 
              className={`block px-3 py-2 rounded-lg text-gray-700 hover:text-pink-dark hover:bg-gray-50 transition-all duration-200 ${isMenuOpen ? 'mobile-menu-item' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              CURSOS
            </Link>
            <Link 
              to="/eventos" 
              className={`block px-3 py-2 rounded-lg text-gray-700 hover:text-pink-dark hover:bg-gray-50 transition-all duration-200 ${isMenuOpen ? 'mobile-menu-item' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              EVENTOS
            </Link>
            <Link 
              to="/" 
              onClick={(e) => handleNavigation(e, 'quienes-somos')}
              className={`block px-3 py-2 rounded-lg text-gray-700 hover:text-pink-dark hover:bg-gray-50 transition-all duration-200 ${isMenuOpen ? 'mobile-menu-item' : ''}`}
            >
              QUIÉNES SOMOS
            </Link>
            <Link 
              to="/" 
              onClick={(e) => handleNavigation(e, 'contacto')}
              className={`block px-3 py-2 rounded-lg text-gray-700 hover:text-pink-dark hover:bg-gray-50 transition-all duration-200 ${isMenuOpen ? 'mobile-menu-item' : ''}`}
            >
              CONTACTO
            </Link>
            {/* Autenticación: se maneja exclusivamente desde el icono de usuario (dropdown). */}
          </div>
        </div>
      </div>

      {/* Cart Sidebar */}
      {isCartOpen && <CartSidebar onClose={closeCart} />}
    </header>
  );
};

export default Header;